import React, { useRef, useCallback, useState, useEffect } from 'react';
import type { Player } from '@/types';
import { GRENADE_MAX_CHARGE_MS } from '@/constants/grenade';

interface MobileActionButtonsProps {
  currentPlayer: Player | undefined;
  grenadeChargeStartTime: number | null;
  isImmobilized: boolean;
  onPrimaryAction: () => void;
  onGrenadeStart: () => void;
  onGrenadeRelease: () => void;
  onGrenadeCancel: () => void;
}

const BTN_SIZE = 'clamp(88px, 22vh, 128px)';
const PRIMARY_BTN_SIZE = 'clamp(101px, 25.3vh, 147px)';
const BTN_GAP = 'clamp(10px, 2.5vh, 16px)';
const ACTION_STACK_BOTTOM = 'max(16px, calc(env(safe-area-inset-bottom, 0px) + 1rem))';
const ACTION_STACK_RIGHT = 'max(220px, calc(env(safe-area-inset-right, 0px) + 1rem))';

const MobileActionButtons: React.FC<MobileActionButtonsProps> = ({
  currentPlayer,
  grenadeChargeStartTime,
  isImmobilized,
  onPrimaryAction,
  onGrenadeStart,
  onGrenadeRelease,
  onGrenadeCancel,
}) => {
  const grenadeTouchIdRef = useRef<number | null>(null);
  const [chargePercent, setChargePercent] = useState(0);

  // Animate grenade charge bar
  useEffect(() => {
    if (grenadeChargeStartTime === null) {
      setChargePercent(0);
      return;
    }
    let raf: number;
    const tick = () => {
      const elapsed = Date.now() - grenadeChargeStartTime;
      const pct = Math.min(elapsed / GRENADE_MAX_CHARGE_MS, 1);
      setChargePercent(pct);
      if (pct < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [grenadeChargeStartTime]);

  const hasTurbo =
    currentPlayer?.turboAvailable && !currentPlayer?.turboUsed && !isImmobilized;
  const netCharges = currentPlayer?.netCharges || 0;
  const hasNet = netCharges > 0 && !isImmobilized;
  const grenadeCharges = currentPlayer?.grenadeCharges || 0;
  const hasGrenade = grenadeCharges > 0;

  // Show primary action button when player has turbo or net
  const showPrimary = hasTurbo || hasNet;

  // Determine primary action label
  let primaryEmoji = '';
  let primaryLabel = '';
  if (hasTurbo) {
    primaryEmoji = '⚡';
    primaryLabel = 'TURBO';
  } else if (hasNet) {
    primaryEmoji = '🕸️';
    primaryLabel = `NET (${netCharges})`;
  }

  // Grenade touch handlers — need press-and-hold
  const handleGrenadeTouchStart = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (grenadeTouchIdRef.current !== null) return;
      grenadeTouchIdRef.current = e.changedTouches[0].identifier;
      onGrenadeStart();
    },
    [onGrenadeStart]
  );

  const handleGrenadeTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      for (let i = 0; i < e.changedTouches.length; i++) {
        if (e.changedTouches[i].identifier === grenadeTouchIdRef.current) {
          grenadeTouchIdRef.current = null;
          onGrenadeRelease();
          return;
        }
      }
    },
    [onGrenadeRelease]
  );

  const handleGrenadeTouchCancel = useCallback(
    (e: React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();
      grenadeTouchIdRef.current = null;
      onGrenadeCancel();
    },
    [onGrenadeCancel]
  );

  const buttonBase: React.CSSProperties = {
    width: BTN_SIZE,
    height: BTN_SIZE,
    borderRadius: '50%',
    border: 'none',
    fontSize: '2rem',
    fontWeight: 'bold',
    cursor: 'pointer',
    touchAction: 'none',
    userSelect: 'none',
    WebkitUserSelect: 'none',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    lineHeight: 1.1,
    position: 'relative',
    boxShadow: '0 2px 10px rgba(0,0,0,0.4)',
  };

  const primaryButtonStyle: React.CSSProperties = hasTurbo
    ? {
        ...buttonBase,
        width: PRIMARY_BTN_SIZE,
        height: PRIMARY_BTN_SIZE,
        background: 'linear-gradient(160deg, rgba(255,224,102,0.96) 0%, rgba(255,191,0,0.94) 60%, rgba(217,119,6,0.92) 100%)',
        color: '#111827',
        border: '3px solid rgba(255,255,255,0.35)',
        boxShadow: '0 10px 28px rgba(95, 62, 0, 0.36)',
      }
    : {
        ...buttonBase,
        width: PRIMARY_BTN_SIZE,
        height: PRIMARY_BTN_SIZE,
        background: 'linear-gradient(165deg, rgba(43,235,136,0.98) 0%, rgba(22,181,95,0.95) 58%, rgba(5,120,64,0.96) 100%)',
        color: '#FFFFFF',
        border: '3px solid rgba(255,255,255,0.42)',
        boxShadow: '0 12px 28px rgba(3, 84, 47, 0.42)',
      };

  return (
    <>
      <div
        style={{
          position: 'fixed',
          bottom: ACTION_STACK_BOTTOM,
          right: ACTION_STACK_RIGHT,
          display: 'flex',
          flexDirection: 'column',
          gap: BTN_GAP,
          alignItems: 'flex-end',
          zIndex: 1000,
          touchAction: 'none',
        }}
      >
        {/* Grenade button (press-and-hold) */}
        {hasGrenade && (
          <button
            onTouchStart={handleGrenadeTouchStart}
            onTouchEnd={handleGrenadeTouchEnd}
            onTouchCancel={handleGrenadeTouchCancel}
            style={{
              ...buttonBase,
              background:
                grenadeChargeStartTime !== null
                  ? 'rgba(255, 68, 68, 0.9)'
                  : 'rgba(255, 107, 107, 0.7)',
              color: '#fff',
              transform:
                grenadeChargeStartTime !== null ? 'scale(1.1)' : 'scale(1)',
              transition: 'transform 0.15s ease, background 0.15s ease',
            }}
          >
            <span style={{ fontSize: '2.5rem' }}>💣</span>
            <span style={{ fontSize: '0.9rem', marginTop: 2 }}>
              {grenadeCharges}
            </span>
            {/* Charge bar overlay */}
            {grenadeChargeStartTime !== null && (
              <div
                style={{
                  position: 'absolute',
                  bottom: -6,
                  left: '10%',
                  width: '80%',
                  height: 5,
                  borderRadius: 3,
                  background: 'rgba(0,0,0,0.4)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: `${chargePercent * 100}%`,
                    height: '100%',
                    borderRadius: 3,
                    background:
                      chargePercent > 0.7
                        ? '#ff4444'
                        : chargePercent > 0.4
                          ? '#ffaa00'
                          : '#ffdd00',
                    transition: 'width 0.05s linear',
                  }}
                />
              </div>
            )}
          </button>
        )}

        {/* Primary action button (turbo or net) */}
        {showPrimary && (
          <button
            onTouchStart={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onPrimaryAction();
            }}
            style={primaryButtonStyle}
          >
            <span
              style={{
                fontSize: hasNet ? '3rem' : '2.9rem',
                filter: hasNet
                  ? 'drop-shadow(0 2px 5px rgba(0,0,0,0.55))'
                  : 'drop-shadow(0 1px 3px rgba(255,255,255,0.18))',
              }}
            >
              {primaryEmoji}
            </span>
            <span
              style={{
                fontSize: hasNet ? '0.95rem' : '0.9rem',
                marginTop: 6,
                padding: hasNet ? '0.34rem 0.7rem' : '0',
                borderRadius: hasNet ? '999px' : 0,
                background: hasNet ? 'rgba(6, 33, 20, 0.34)' : 'transparent',
                boxShadow: hasNet
                  ? 'inset 0 0 0 1px rgba(255,255,255,0.22)'
                  : 'none',
                color: hasTurbo ? '#111827' : '#FFFFFF',
                fontWeight: 800,
                letterSpacing: hasNet ? '0.02em' : 'normal',
                textShadow: hasNet ? '0 1px 2px rgba(0,0,0,0.55)' : 'none',
              }}
            >
              {primaryLabel}
            </span>
          </button>
        )}
      </div>
    </>
  );
};

export default MobileActionButtons;
